import {
  Food,
  FoodStatus,
  getFoodReferenceWeight,
  isUsableNutrientValue,
  Nutrients,
  usableNutrientNumber,
} from './food';

export type Ingredient = IngredientReal | IngredientMacro;

export enum IngredientType {
  Real = 0,
  Custom = 1,
}

export interface IngredientShared {
  type: IngredientType;
  weight_g: number;
  weight_unit?: string;
  status?: FoodStatus;
  kcal?: number; /// cached value for quick display (TODO remove this?!)
}

export interface IngredientReal extends IngredientShared {
  type: IngredientType.Real;
  foodId: string;
  custom_unit?: string;
  // NOTE: The below are cached so that we don't have to call getFoodByIdAsync(ingredient.foodId) for each, while rendering lists
  // Perhaps we want to remove that, as it does clog the dabatase with redundant (and possibly stale) data?
  // (see https://gitlab.com/cybernetic-fitness/cybernetic-fitness-client/issues/17 for discussion)
  name: string; /// cached value for quick display (TODO remove this?!)
  locked?: boolean;
  /** Set when the person weighed this food with the part they do not eat (peel, pit, bone, shell),
   * #5544. `weight_g` is still the EATEN weight, so every nutrient sum reads it unchanged; `percent`
   * is the food's uneaten share when the box was ticked, copied here so a later correction to the
   * food does not change an old entry. The weight as typed is worked back as
   * `weight_g / (1 - percent / 100)` (`typedWeight` in `client/src/pages/edit-ingredient/weighed-with-refuse.ts`). */
  weighedWithRefuse?: { percent: number };
}

export function isIngredientReal(i: Ingredient): i is IngredientReal {
  return i != null && (i.type === IngredientType.Real || i.type === undefined);
}

export interface IngredientMacro extends IngredientShared {
  type: IngredientType.Custom;
  name: string;
  nutrients: Nutrients;
  status: FoodStatus.Custom;
}

export function isIngredientMacro(i: Ingredient): i is IngredientMacro {
  return i != null && i.type === IngredientType.Custom;
}

/**
 * What one of the scaling helpers below had to treat as unusable.
 *
 * The helpers guard against a stored value they cannot compute with, and the
 * guard is the right behaviour: a value that is not a number counts as zero
 * rather than turning the answer into NaN and spreading it through every total
 * computed from it. What the guard cannot do by itself is SAY that it fired.
 * `shared/` has no Sentry SDK and no reporting helper of its own, so a helper
 * here cannot report anything -- it can only hand back what it skipped, and let
 * a caller that does have a `LoggingService` report it.
 *
 * Pass one of these in to find out; leave it out and the helpers stay exactly as
 * silent as they were. On the client, `reportUnusableNutrientInput` in
 * `client/src/util/nutrient-data-quality-reporter.ts` is what turns a filled
 * collector into a Sentry event.
 */
export interface UnusableNutrientInput {
  /** nutrient keys whose stored value was not a number, and were therefore counted as zero */
  nutrientKeys: string[];
  /**
   * Set when the ingredient's own weight could not be multiplied by, so the
   * stored numbers were handed back unscaled.
   *
   * This is a separate flag rather than "is `unusableWeight_g` set?", because
   * the commonest unusable weight is `undefined` itself -- a field missing from
   * an old or damaged document. Asking whether the VALUE is undefined would
   * answer "nothing was wrong" in exactly the case that matters most.
   */
  weightWasUnusable?: boolean;
  /**
   * What that weight actually was, so a report can say which kind of bad value it
   * was. Typed `unknown` on purpose: the whole point is that it was not the
   * `number` the type claims. Meaningless unless `weightWasUnusable` is set.
   */
  unusableWeight_g?: unknown;
  /**
   * Which ingredients each recorded nutrient key came from, as labels the caller chose
   * (a food id, or a typed-in ingredient's position). Only `mergeUnusableNutrientInput`
   * fills this. The scaling helpers do not know which ingredient they are scaling, so a
   * caller that adds up a whole meal scales each ingredient into its own collector and
   * merges it in with a label -- otherwise the report names the nutrient but not the
   * food that held it, and cannot be traced (#6322).
   */
  sourcesByNutrientKey?: Record<string, string[]>;
  /** As `sourcesByNutrientKey`, for the ingredients whose weight was unusable. */
  unusableWeightSources?: string[];
}

/** A fresh, empty collector. Callers use this rather than writing the shape out. */
export function newUnusableNutrientInput(): UnusableNutrientInput {
  return { nutrientKeys: [] };
}

/**
 * Add what one ingredient's collector recorded into a whole meal's collector, remembering
 * `source` as the ingredient it came from. See `sourcesByNutrientKey` for why.
 *
 * Safe to call with an empty `from`, which is the normal case: it then changes nothing.
 */
export function mergeUnusableNutrientInput(into: UnusableNutrientInput, from: UnusableNutrientInput, source: string): void {
  for (const nutrientKey of from.nutrientKeys) {
    into.nutrientKeys.push(nutrientKey);
    into.sourcesByNutrientKey = into.sourcesByNutrientKey || {};
    into.sourcesByNutrientKey[nutrientKey] = addSourceOnce(into.sourcesByNutrientKey[nutrientKey], source);
  }

  if (from.weightWasUnusable) {
    into.weightWasUnusable = true;
    into.unusableWeight_g = from.unusableWeight_g;
    into.unusableWeightSources = addSourceOnce(into.unusableWeightSources, source);
  }
}

/**
 * `sources` with `source` added unless it is already there. A damaged value is recorded twice
 * for one ingredient (once when scaled, once when added into the total), and the report should
 * name that ingredient once.
 */
function addSourceOnce(sources: string[] | undefined, source: string): string[] {
  const list = sources || [];
  if (!list.includes(source)) list.push(source);
  return list;
}

/** Did any of the helpers actually record something in this collector? */
export function hasUnusableNutrientInput(unusable: UnusableNutrientInput): boolean {
  return unusable.nutrientKeys.length > 0 || unusable.weightWasUnusable === true;
}

/**
 * What a stored nutrient map contributes at a given weight.
 *
 * This is the one statement of a rule the app applies everywhere it turns an
 * ingredient into "how much protein, fat and carbohydrate does this actually
 * add?". Stored nutrients are always numbers per some reference weight, and the
 * only thing that varies is which weight that is:
 *
 *  - an ordinary food stores its numbers per 100 g, so the reference weight is 100;
 *  - a recipe stores its numbers per the whole recipe, so the reference weight is
 *    the recipe's own total weight -- `getFoodReferenceWeight` answers both, and
 *    `realIngredientNutrientsForWeight` below is that case spelled out;
 *  - a typed-in ("macro") ingredient stores per 100 g, like an ordinary food;
 *  - an ingredient standing in for an already-logged meal carries ABSOLUTE
 *    numbers and is not passed through here at all -- see `addNutrientsInto`.
 *
 * A stored value that is not a number counts as zero rather than turning the
 * result into NaN, which would otherwise spread through every total computed
 * from it.
 *
 * A food read from the database CAN be in that state: a record's values are JSON, and a
 * string is perfectly valid JSON. `normalizeFoodFormat` repairs a nutrient stored as a
 * numeric string as the food is read, so what still reaches this guard is a value no
 * repair could invent an answer for -- `undefined`, NaN, an object, an empty string, or
 * a marker string. It also guards a map built in memory, which nothing normalises.
 *
 * Pass `unusable` to be told when that guard fires; the nutrient key lands in
 * `unusable.nutrientKeys`. Leave it out and the guard stays silent, which is
 * what every caller that has nowhere to report to should do. See
 * `UnusableNutrientInput` for why this helper cannot report for itself.
 *
 * This helper does NOT check the weight it is given. Callers differ on what a
 * missing or nonsensical weight should mean, and each one says so for itself:
 * `macroIngredientNutrientsForWeight` hands the numbers back untouched, while
 * the amount screen skips the ingredient entirely before it gets here.
 */
export function nutrientsForWeight(
  nutrients: Nutrients,
  weight_g: number,
  referenceWeight_g: number,
  unusable?: UnusableNutrientInput,
): Nutrients {
  const scaled: Nutrients = {};
  for (const nutrientKey of Object.keys(nutrients || {})) {
    const stored = nutrients[nutrientKey];
    // a value we cannot multiply counts as zero; record it so somebody can report it
    if (unusable && !isUsableNutrientValue(stored)) {
      unusable.nutrientKeys.push(nutrientKey);
    }
    scaled[nutrientKey] = (usableNutrientNumber(stored) * weight_g) / referenceWeight_g;
  }
  return scaled;
}

/**
 * `nutrientsForWeight` for a map that is going to be SHOWN rather than added up: a stored
 * `null` -- a nutrient nobody measured -- stays `null` instead of becoming `0`.
 *
 * `FormatNutrients` shows `--` for a `null`, but once `nutrientsForWeight` has turned it
 * into a real zero nothing at the display can tell, and the screen states a measurement the
 * record never made (df-2b3573b7206a). A damaged value (`undefined`, NaN, a string, an
 * object) becomes `null` too: it is no measurement either, and `FormatNutrients` already
 * shows `--` for it when handed the food's own map. Every real number scales exactly as
 * `nutrientsForWeight` scales it, and `unusable` reports exactly the same keys.
 *
 * Never add a map from here into a total: a sum still counts an unmeasured value as zero,
 * and `nutrientsForWeight` is the helper for that.
 */
export function nutrientsForWeightForDisplay(
  nutrients: Nutrients,
  weight_g: number,
  referenceWeight_g: number,
  unusable?: UnusableNutrientInput,
): Nutrients {
  const scaled = nutrientsForWeight(nutrients, weight_g, referenceWeight_g, unusable);
  for (const nutrientKey of Object.keys(scaled)) {
    const stored = nutrients[nutrientKey];
    // if nobody measured it (or the value is damaged), keep saying so; `Infinity` is a
    // number and scales through, as `usableNutrientNumber` describes
    if (stored === null || !isUsableNutrientValue(stored)) {
      scaled[nutrientKey] = null;
    }
  }
  return scaled;
}

/**
 * What a real food contributes at a given weight -- `nutrientsForWeight` with the
 * reference weight the food itself declares.
 *
 * Read the food with `getFoodByIdAsync` first, which repairs a recipe whose
 * stored total weight is missing; `getFoodReferenceWeight` falls back to 100 when
 * it still is.
 */
export function realIngredientNutrientsForWeight(
  food: Food,
  weight_g: number,
  unusable?: UnusableNutrientInput,
): Nutrients {
  return nutrientsForWeight(food.nutrients, weight_g, getFoodReferenceWeight(food), unusable);
}

/**
 * Add one ingredient's contribution into a running total, in place.
 *
 * Pass already-absolute numbers straight in: a logged meal's stored nutrients are
 * the totals of what was eaten, so they are added as they are rather than scaled.
 *
 * This one matters for reporting more than it looks. A logged meal's absolute
 * numbers never go through `nutrientsForWeight` at all, so this is the ONLY place
 * that sees them -- pass `unusable` and a damaged value in one of those becomes
 * visible; leave it out and nothing anywhere would ever notice.
 */
export function addNutrientsInto(total: Nutrients, contribution: Nutrients, unusable?: UnusableNutrientInput): void {
  for (const nutrientKey of Object.keys(contribution || {})) {
    const added = contribution[nutrientKey];
    if (unusable && !isUsableNutrientValue(added)) {
      unusable.nutrientKeys.push(nutrientKey);
    }
    // the running total is a number this function wrote itself, so `|| 0` is right for
    // it -- only the CONTRIBUTION comes from a stored record and needs the full guard
    total[nutrientKey] = (total[nutrientKey] || 0) + usableNutrientNumber(added);
  }
}

/**
 * Add one ingredient into a meal's running total, and record what could not be used
 * (df-bb517784c49c).
 *
 * This is the one statement of what one ingredient contributes to a meal total. The saved
 * meal total, the ingredient list's live total and the amount screen's meal total all call it,
 * so a change to the rule is made once. Each of them still decides for itself what to skip
 * BEFORE calling it (a missing weight, a missing food, a food with no nutrient map), because
 * those rules really do differ between them.
 *
 * - A real ingredient is scaled from `food`, by the reference weight the food declares (a
 *   recipe by its own total weight, an ordinary food by 100 g). The caller must already have
 *   read the food; passing none is a programming mistake and throws.
 * - A typed-in ingredient stores its numbers per 100 g, so it is scaled from its own map by
 *   100 and `food` is ignored. This is deliberately NOT `macroIngredientNutrientsForWeight`,
 *   whose fallback hands the stored numbers back unscaled for a weight it cannot use; a total
 *   is better short by an ingredient than wrong by a whole portion.
 *
 * The ingredient scales into its own collector, and that collector is merged into
 * `mealUnusable` under `source`, so the meal's one report can say which food held a bad
 * value (#6322). The caller builds `source`, because the wording lives with the reporting.
 *
 * @returns what this ingredient contributed, for callers that also cache or display it
 */
export function addIngredientIntoMealTotal(
  total: Nutrients,
  ingredient: Ingredient,
  food: Food | null | undefined,
  mealUnusable: UnusableNutrientInput,
  source: string,
): Nutrients {
  const ingredientUnusable = newUnusableNutrientInput();
  let scaled: Nutrients;
  if (isIngredientMacro(ingredient)) {
    scaled = nutrientsForWeight(ingredient.nutrients, ingredient.weight_g, 100, ingredientUnusable);
  } else {
    if (!food) {
      throw new Error(`addIngredientIntoMealTotal: no food was passed for the real ingredient ${ingredient.foodId}`);
    }
    scaled = realIngredientNutrientsForWeight(food, ingredient.weight_g, ingredientUnusable);
  }
  addNutrientsInto(total, scaled, ingredientUnusable);
  mergeUnusableNutrientInput(mealUnusable, ingredientUnusable, source);
  return scaled;
}

/**
 * Whether a weight can be multiplied by: a real number, not infinite, and not negative.
 *
 * `unknown` on purpose -- the weight on a stored ingredient may be missing or damaged, and
 * the amount screen's field hands back `undefined` while it is empty or holds text that is
 * not a number (df-6aedbb363d8c).
 */
export function isUsableIngredientWeight(weight_g: unknown): weight_g is number {
  return typeof weight_g === 'number' && isFinite(weight_g) && weight_g >= 0;
}

/**
 * How much a typed-in ("macro") ingredient actually contributes.
 *
 * A typed-in ingredient stores its numbers PER 100 g, so what it really
 * contributes is those numbers times its weight over 100. Everything that
 * displays a meal already works that way -- `recalculateMealNutrientsAsync`
 * computes `nutrients * weight_g / 100` for exactly this kind of ingredient.
 *
 * The meal tuner is the one place that did not, because it never needed to:
 * every path that creates a typed-in ingredient set its weight to 100 g and the
 * app offers no way to change it, so times-one was always the right answer. That
 * stopped being true when splitting a recipe started scaling a typed-in
 * ingredient down to the portion the user logged (#4427), which is why this
 * exists. At 100 g it returns the same numbers it was given, so no meal that
 * could be built before behaves any differently.
 *
 * A weight we cannot multiply by -- missing, negative, or not a real number --
 * gives the numbers back untouched rather than turning them into NaN, which
 * would poison every total computed from them.
 *
 * Pass `unusable` to be told about either kind of damage: the refused weight
 * lands in `unusable.weightWasUnusable`, and an unusable nutrient VALUE lands in
 * `unusable.nutrientKeys` because the collector travels down into
 * `nutrientsForWeight`.
 */
export function macroIngredientNutrientsForWeight(
  ingredient: IngredientMacro,
  unusable?: UnusableNutrientInput,
): Nutrients {
  const nutrients = ingredient.nutrients || {};
  const weight_g = ingredient.weight_g;
  if (!isUsableIngredientWeight(weight_g)) {
    // handing the numbers back unscaled is deliberate, but it is also a wrong answer
    // for any weight other than 100 g -- record it so somebody can report it
    if (unusable) {
      unusable.weightWasUnusable = true;
      unusable.unusableWeight_g = weight_g;
    }
    return nutrients;
  }

  // per 100 g, like an ordinary food -- `nutrientsForWeight` is the rule itself
  return nutrientsForWeight(nutrients, weight_g, 100, unusable);
}

/**
 * Decide whether two ingredient lists contain the same *set* of real foods.
 *
 * This answers the question "is this meal still the same recipe it was based
 * on?". A meal keeps its recipe link (`basedOnFoodId`) only while its foods
 * still match that recipe. We compare the *set* of `foodId`s of the real
 * ingredients:
 *  - amounts (weight) are ignored, so changing how much of a food you eat still
 *    counts as the same recipe;
 *  - adding, removing, or swapping any food makes the sets differ, so it is no
 *    longer the same recipe;
 *  - custom macro-only ingredients (no `foodId`) are ignored, matching the rest
 *    of the meal-vs-recipe comparison logic which only looks at real foods.
 *
 * Returns true when the two lists share the exact same set of real `foodId`s.
 */
export function ingredientFoodSetsMatch(ingredientsA: Ingredient[], ingredientsB: Ingredient[]): boolean {
  const realFoodIdSet = (ingredients: Ingredient[]): Set<string> => {
    const foodIds = new Set<string>();
    for (const ingredient of ingredients) {
      if (isIngredientReal(ingredient)) {
        foodIds.add(ingredient.foodId);
      }
    }
    return foodIds;
  };

  const setA = realFoodIdSet(ingredientsA);
  const setB = realFoodIdSet(ingredientsB);

  // if the two sets differ in size they cannot be equal
  if (setA.size !== setB.size) {
    return false;
  }

  // for each food in one set, it must also appear in the other
  for (const foodId of setA) {
    if (!setB.has(foodId)) {
      return false;
    }
  }

  return true;
}
